import { useTranslation } from "react-i18next";

import { DownloadIcon, ImagesIcon } from "lucide-react";

import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import { allowedImageTypesList } from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { styled } from "simplydo/react/styled";
import { ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent, CardHeader } from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";

import { ImageChooser } from "components/lib/Choosers";
import { UploadedFilesTable } from "components/lib/UploadedFilesTable";

import { FieldProps } from "./FieldProps";

const StyledImage = styled.img`
  max-width: 100%;
  display: block;
  object-fit: contain;
`;

const ImageComponent = ({ image }: { image: string }) => {
  const { t } = useTranslation();

  return (
    <Card style={{ width: "80%" }}>
      <CardHeader>
        <Icon icon={ImagesIcon} />
        Uploaded image
      </CardHeader>
      <CardContent style={{ textAlign: "center" }}>
        <CardHeader style={{ overflowWrap: "break-word" }}>
          <StyledImage
            alt={`${t("common:capitalise", { key: "generic.idea" })} field content`}
            src={image}
          />
        </CardHeader>
      </CardContent>
      <CardContent>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <ButtonLink
            fluid
            style={{ marginLeft: 0, marginRight: 0 }}
            size="small"
            variant="outline"
            to={image}
            target="_blank"
            rel="noopener noreferrer"
            download={image}
          >
            <Icon icon={DownloadIcon} />
            {t("generic.download")}
          </ButtonLink>
        </div>
      </CardContent>
    </Card>
  );
};

const Image = ({
  field,
  ideaId,
  fieldValue = "",
  updateIdea,
  isEditing,
}: FieldProps) => {
  const imageSrc = typeof fieldValue === "string" ? fieldValue : "";
  if (!isEditing) {
    if (imageSrc) {
      return <ImageComponent image={imageSrc} />;
    }
    return null;
  }

  if (imageSrc) {
    return (
      <UploadedFilesTable
        variant="image"
        files={[{ id: imageSrc, url: imageSrc }]}
        onRemove={() => updateIdea(null, ["templated", field.id])}
      />
    );
  }

  return (
    <div>
      <ImageChooser
        trigger={
          <FileChooserDropzoneTrigger
            forType="idea"
            accept={allowedImageTypesList.join(",")}
            forId={ideaId}
            onComplete={(_value, _name, imageSrc) =>
              updateIdea(imageSrc, ["templated", field.id])
            }
            dropzoneProps={{
              title: "Upload an image",
              defaultIcon: ImagesIcon,
            }}
          />
        }
        forType="idea"
        forId={ideaId}
        onComplete={(_value, _name, imageSrc) =>
          updateIdea(imageSrc, ["templated", field.id])
        }
      />
      {imageSrc ? (
        <UploadedFilesTable
          variant="image"
          files={[{ id: imageSrc, url: imageSrc }]}
          onRemove={() => updateIdea(null, ["templated", field.id])}
        />
      ) : null}
    </div>
  );
};

export default Image;
